import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { QueryDTOForEcommerceTestimonials, Testimonial } from "../types";
import { getCookie } from "../../../utility/getCookie";

const BASE_URL =
  `${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_CUSTOMER_APP_BACKEND_PREFIX}`;

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_CUSTOMER_APP_BACKEND_PREFIX) {
  console.warn(
    "Warning: REACT_APP_CUSTOMER_APP_BACKEND_PREFIX is not defined."
  );
}


// Axios instance with cookies support
const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    let token = getCookie("access_token");
    if (token === null || token === undefined) {
      try {
        token = getCookie("access_token");
      } catch (err) {
        // Handle refresh failure (e.g., redirect to login)
      }
    }
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const fetchEcommerceCustomerTestimonials = createAsyncThunk(
  "testimonials/fetchEcommerceCustomerTestimonials",
  async (query: QueryDTOForEcommerceTestimonials, { rejectWithValue }) => {
    try {
      const params: Record<string, any> = {};
      if (query.page) params.page = query.page;
      if (query.limit) params.limit = query.limit;
      const response = await api.get(`/ecommerce/testimonials`, {
        params,
      });
      // console.log(response.data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const fetchEcommerceSellerTestimonials = createAsyncThunk(
  "testimonials/fetchEcommerceSellerTestimonials",
  async (query: QueryDTOForEcommerceTestimonials, { rejectWithValue }) => {
    try {
      const params: Record<string, any> = {};
      if (query.page) params.page = query.page;
      if (query.limit) params.limit = query.limit;
      params.createdBy = "seller";
      const response = await api.get(`/testimonials`, {
        params,
      });
      // console.log(response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  }
);
export const fetchEcommerceTestimonialById = createAsyncThunk(
  "testimonials/fetchEcommerceTestimonialById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/testimonials/${id}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  }
);


interface EcommerceTestimonialsState {
  customerTestimonials: Testimonial[];
  sellerTestimonials: Testimonial[];
  currentTestimonial: Testimonial | null;
  isLoading: boolean;
  error: string | null;
  success: boolean;
}
const initialState: EcommerceTestimonialsState = {
  customerTestimonials: [],
  sellerTestimonials: [],
  currentTestimonial: null,
  isLoading: false,
  error: null,
  success: false,
}

const ecommerceTestimonialsSlice = createSlice({
  name: "ecommerceTestimonials",
  initialState,
  reducers: {
    clearError: (state: EcommerceTestimonialsState) => {
      state.error = null;
    },
    clearSuccess: (state: EcommerceTestimonialsState) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEcommerceCustomerTestimonials.pending, (state: EcommerceTestimonialsState) => {
      state.isLoading = true;
      state.error = null;
      state.success = false;
    })
      .addCase(fetchEcommerceCustomerTestimonials.fulfilled, (state: EcommerceTestimonialsState, action: PayloadAction<Testimonial[]>) => {
        state.isLoading = false;
        state.customerTestimonials = action.payload;
        state.success = true;
      })
      .addCase(fetchEcommerceCustomerTestimonials.rejected, (state: EcommerceTestimonialsState, action: PayloadAction<unknown>) => {
        state.isLoading = false;
        // state.error = action.payload as string;
        state.success = false;
      })
      .addCase(fetchEcommerceSellerTestimonials.pending, (state: EcommerceTestimonialsState) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchEcommerceSellerTestimonials.fulfilled, (state: EcommerceTestimonialsState, action: PayloadAction<Testimonial[]>) => {
        state.isLoading = false;
        state.sellerTestimonials = action.payload;
        state.success = true;
      })
      .addCase(fetchEcommerceSellerTestimonials.rejected, (state: EcommerceTestimonialsState, action: PayloadAction<unknown>) => {
        state.isLoading = false;
        // state.error = action.payload as string;
        state.success = false;
      })
      .addCase(fetchEcommerceTestimonialById.pending, (state: EcommerceTestimonialsState) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchEcommerceTestimonialById.fulfilled, (state: EcommerceTestimonialsState, action: PayloadAction<Testimonial>) => {
        state.isLoading = false;
        state.currentTestimonial = action.payload;
        state.success = true;
      })
      .addCase(fetchEcommerceTestimonialById.rejected, (state: EcommerceTestimonialsState, action: PayloadAction<unknown>) => {
        state.isLoading = false;
        // state.error = action.payload as string;
        state.success = false;
      })

  }
});

export const { clearError, clearSuccess } = ecommerceTestimonialsSlice.actions;
export default ecommerceTestimonialsSlice.reducer;